import React, { useState, useEffect, useRef } from "react"
import { XMarkIcon, Bars3Icon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import ContentEditor from "aldoo-ra/CMS/content-editor/index"
import Modal from "aldoo-ra/Modal"
import { useFieldDragController } from "aldoo-ra/CMS/controllers/field-drag-controller"
import { showNotification } from "aldoo-ra/Notification"
import _ from "lodash"
import { formatMoney, useStoreCurrency } from "aldoo-ra/Store/currency"

function SubscriptionPhaseEditor({
  onChange,
  value,
  field,
  parentValues,
  parentObject,
}) {
  const [localValue, setLocalValue] = useState(value ?? [])
  // a new phase is priced in the store's currency (STORE_CURRENCY)
  const { currency: storeCurrency } = useStoreCurrency()
  const [showDialog, setShowDialog] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [itemContentValues, setItemContentValues] = useState({})
  const isInternalChange = useRef(false)

  const labelField = field?.item?.labelField || "phase_type"

  // Unified function to update value and notify parent
  const updateValue = (newValue) => {
    isInternalChange.current = true
    setLocalValue(newValue)
    onChange?.(newValue)
  }

  const { handleDragStart, handleDragOver, handleDragEnd } =
    useFieldDragController(localValue, updateValue)

  // Handle external value changes
  useEffect(() => {
    if (!isInternalChange.current && !_.isEqual(value, localValue)) {
      setLocalValue(value ?? [])
    }
    isInternalChange.current = false
  }, [value])

  const addNewInput = () => {
    const newItem = {
      phase_type: "regular",
      enabled: true,
      amount: 0,
      currency: storeCurrency,
      interval: "month",
      interval_count: 1,
      requires_payment_method: true
    }
    const newIndex = localValue.length
    const updatedValues = [...localValue, newItem]
    updateValue(updatedValues)
    setSelectedIndex(newIndex)
    setItemContentValues(newItem)
    setShowDialog(true)
  }

  const removeInput = (index) => {
    const updatedValues = localValue.filter((_, i) => i !== index)
    updateValue(updatedValues)
  }

  const clearAll = () => {
    updateValue([])
  }

  const handleEdit = (index) => {
    setSelectedIndex(index)
    setItemContentValues(localValue[index] || {})
    setShowDialog(true)
  }

  const handleCloseDialog = () => {
    setShowDialog(false)
    setSelectedIndex(null)
  }

  const handleSaveItem = (newValues) => {
    const updatedValues = [...localValue]
    updatedValues[selectedIndex] = newValues
    updateValue(updatedValues)
  }

  const togglePhaseEnabled = (index) => {
    const updatedValues = [...localValue]
    updatedValues[index].enabled = !updatedValues[index].enabled
    updateValue(updatedValues)
  }

  // Helper functions for display
  const getPhaseIcon = (phaseType) => {
    switch (phaseType) {
      case 'trial':
        return '🔍'
      case 'introductory':
        return '⭐'
      case 'regular':
        return '💰'
      default:
        return '📋'
    }
  }

  const getPhaseLabel = (phaseType) => {
    return phaseType.charAt(0).toUpperCase() + phaseType.slice(1)
  }

  const formatPrice = (amount, currency) => {
    if (amount === 0) return 'Free'
    return formatMoney(amount, currency?.toUpperCase(), { cents: true, locale: 'en' })
  }

  const formatDuration = (iterations, interval, intervalCount) => {
    if (!iterations) return 'Ongoing'

    const intervalLabel = intervalCount > 1 ? `${intervalCount} ${interval}s` : interval
    const cycleLabel = iterations === 1 ? 'cycle' : 'cycles'

    return `${iterations} ${cycleLabel} (${intervalLabel})`
  }

  const renderPhaseTimeline = () => {
    const enabledPhases = localValue.filter(p => p.enabled)
    if (enabledPhases.length === 0) return null

    return (
      <div className="bg-admin_canvas rounded-lg p-4 mb-4">
        <div className="text-xs text-admin_text_secondary font-medium mb-3 uppercase tracking-wide">
          Subscription Timeline (Enabled Phases Only)
        </div>
        <div className="flex items-center gap-2 overflow-x-auto">
          {enabledPhases.map((phase, index) => (
            <React.Fragment key={index}>
              <div className="flex-shrink-0 bg-admin_paper border border-admin_bordered rounded-lg px-3 py-2 min-w-[140px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{getPhaseIcon(phase.phase_type)}</span>
                  <span className="text-xs font-medium text-admin_text">
                    {getPhaseLabel(phase.phase_type)}
                  </span>
                </div>
                <div className="text-xs text-admin_text_secondary">
                  {formatPrice(phase.amount, phase.currency)}
                </div>
                <div className="text-xs text-admin_text_secondary">
                  {formatDuration(phase.iterations, phase.interval, phase.interval_count)}
                </div>
              </div>
              {index < enabledPhases.length - 1 && (
                <div className="flex-shrink-0 text-admin_text_secondary">→</div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    )
  }

  const renderItem = (item, index) => {
    const isEnabled = item.enabled !== false

    return (
      <div
        key={index}
        className="w-full mt-3 first:mt-0"
        draggable={localValue?.length > 1}
        onDragStart={localValue?.length > 1 ? () => handleDragStart(index) : undefined}
        onDragOver={localValue?.length > 1 ? (e) => {
          e.preventDefault()
          handleDragOver(index)
        } : undefined}
        onDragEnd={localValue?.length > 1 ? handleDragEnd : undefined}
      >
        <div className={`bg-admin_paper border rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200 ${
          isEnabled ? 'border-admin_bordered' : 'border-admin_bordered opacity-60'
        }`}>
          {/* Card Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3 flex-1">
              {localValue.length > 1 && (
                <Bars3Icon className="w-5 h-5 cursor-move text-admin_text_secondary flex-shrink-0" />
              )}

              {/* Phase Icon & Type */}
              <div className="flex items-center gap-2">
                <span className="text-2xl">{getPhaseIcon(item.phase_type)}</span>
                <div>
                  <div className="font-medium text-admin_text">
                    Phase {index + 1}: {getPhaseLabel(item.phase_type)}
                  </div>
                  <div className="text-xs text-admin_text_secondary">
                    {isEnabled ? 'Enabled' : 'Disabled'}
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              {/* Enable/Disable Toggle */}
              <button
                onClick={() => togglePhaseEnabled(index)}
                className="flex items-center gap-1 px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200"
                style={{
                  backgroundColor: isEnabled ? '#10b981' : '#ef4444',
                  color: 'white'
                }}
                title={isEnabled ? 'Click to disable' : 'Click to enable'}
              >
                {isEnabled ? (
                  <>
                    <CheckCircleIcon className="w-4 h-4" />
                    <span>Enabled</span>
                  </>
                ) : (
                  <>
                    <XCircleIcon className="w-4 h-4" />
                    <span>Disabled</span>
                  </>
                )}
              </button>

              <Button
                variant="admin_icon"
                onClick={() => handleEdit(index)}
                className="flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </Button>
              <Button
                variant="admin_icon"
                onClick={() => removeInput(index)}
                className="flex-shrink-0 text-admin_error hover:bg-admin_error hover:text-white"
              >
                <XMarkIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Phase Details */}
          <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t border-admin_bordered">
            <div>
              <div className="text-xs text-admin_text_secondary font-medium mb-1">Price</div>
              <div className="text-sm text-admin_text font-mono">
                {formatPrice(item.amount, item.currency)}
              </div>
            </div>
            <div>
              <div className="text-xs text-admin_text_secondary font-medium mb-1">Duration</div>
              <div className="text-sm text-admin_text">
                {formatDuration(item.iterations, item.interval, item.interval_count)}
              </div>
            </div>
            <div>
              <div className="text-xs text-admin_text_secondary font-medium mb-1">Payment Method</div>
              <div className="text-sm text-admin_text">
                {item.requires_payment_method ? 'Required' : 'Not Required'}
              </div>
            </div>
          </div>

          {/* Stripe Price ID if available */}
          {item.stripe_price_id && (
            <div className="mt-2 text-xs text-admin_text_secondary">
              <span className="font-medium">Stripe Price ID:</span>{' '}
              <span className="font-mono">{item.stripe_price_id}</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="subscription-phase-editor">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-admin_text">
          {localValue.length} {localValue.length === 1 ? "phase" : "phases"}
          {localValue.filter(p => p.enabled).length !== localValue.length && (
            <span className="ml-2 text-admin_text_secondary">
              ({localValue.filter(p => p.enabled).length} enabled)
            </span>
          )}
        </span>
      </div>

      {/* Timeline Visualization */}
      {renderPhaseTimeline()}

      <div className="bg-admin_canvas rounded-lg p-4">
        {localValue.length > 0 ? (
          localValue.map((item, index) => renderItem(item, index))
        ) : (
          <div className="text-center py-8 text-admin_text_secondary">
            <p className="mb-2">No subscription phases configured</p>
            <p className="text-xs">Add phases to configure trial periods, introductory offers, or regular pricing</p>
          </div>
        )}
      </div>

      <div className="flex flex-row justify-end items-center mt-4">
        <div className="flex items-center gap-5">
          <Button
            variant="admin_secondary"
            onClick={addNewInput}
          >
            Add Phase
          </Button>

          {localValue.length > 0 && (
            <Button
              variant="admin_secondary"
              onClick={clearAll}
            >
              Clear All
            </Button>
          )}
        </div>
      </div>

      {showDialog && field.item && (
        <Modal
          isOpen={showDialog}
          onClose={handleCloseDialog}
          closeOnEscape
          variant="admin"
        >
          <ContentEditor
            title={`Edit Phase ${selectedIndex + 1}: ${getPhaseLabel(itemContentValues.phase_type || 'regular')}`}
            contentType={field.item}
            parentValues={parentValues}
            fieldValues={itemContentValues}
            onSave={handleSaveItem}
            onClickSave={handleCloseDialog}
            onError={(errors) => {
              showNotification({
                message: Object.values(errors).find((e) => e),
                className: "bg-admin_error text-white",
              })
            }}
          />
        </Modal>
      )}
    </div>
  )
}

export default SubscriptionPhaseEditor
